import React, { useState, useEffect, useMemo } from "react";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import CheckIcon from "@mui/icons-material/Check";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

import type { WidgetConfig, WidgetPhase, CustomQuestion } from "./types";
import { evalCondition } from "./utils";
import { fmt } from "./utils";

const WidgetPreview: React.FC<{ config: WidgetConfig }> = ({ config }) => {
  const [open, setOpen] = useState(true);
  const [tab, setTab] = useState("bugReport");
  const [phase, setPhase] = useState<WidgetPhase>("form");
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [hasScreenshot, setHasScreenshot] = useState(false);
  const [recSec, setRecSec] = useState(0);
  const [recPaused, setRecPaused] = useState(false);
  const [micOn, setMicOn] = useState(false);
  const [hasRecording, setHasRecording] = useState(false);

  const bc = config.brandColor || "#FF8C21";
  const dk = config.darkMode;
  const bg = dk ? "#1A1F26" : "#FFFFFF";
  const tx = dk ? "#E8ECF1" : "#1A2332";
  const tx2 = dk ? "#8899AA" : "#6B7B8E";
  const tx3 = dk ? "#556677" : "#99A8B8";
  const bd = dk ? "#2A3544" : "#E5E9EF";
  const inp = dk ? "#111820" : "#F5F7FA";
  const crd = dk ? "#222A36" : "#F8F9FB";

  useEffect(() => {
    if (phase !== "recording" || recPaused) return;
    const t = setInterval(
      () =>
        setRecSec((p) => {
          if (p + 1 >= (config.maxRecordSec || 120)) {
            setPhase("review");
            return p;
          }
          return p + 1;
        }),
      1000,
    );
    return () => clearInterval(t);
  }, [phase, recPaused, config.maxRecordSec]);

  useEffect(() => {
    if (!open) {
      setPhase("form");
      setRecSec(0);
      setRecPaused(false);
      setHasRecording(false);
      setHasScreenshot(false);
      setFormData({});
    }
  }, [open]);

  const tabs = useMemo(() => {
    const t: { id: string; label: string; color: string }[] = [];
    if (config.categories.bugReport)
      t.push({ id: "bugReport", label: "Bug Report", color: "#FF4D6A" });
    if (config.categories.generalFeedback)
      t.push({ id: "generalFeedback", label: "Feedback", color: "#3B82F6" });
    if (config.categories.featureRequest)
      t.push({ id: "featureRequest", label: "Feature", color: "#FBBF24" });
    return t;
  }, [config.categories]);

  useEffect(() => {
    if (tabs.length && !tabs.find((t) => t.id === tab)) setTab(tabs[0].id);
  }, [tabs, tab]);

  const allQs = config.customQuestions.filter(
    (q) => q.category === tab || q.category === "all",
  );
  const visibleQs = allQs.filter((q) => evalCondition(q.condition, formData));
  const maxSec = config.maxRecordSec || 120;
  const recPct = Math.min((recSec / maxSec) * 100, 100);

  const updateField = (qId: string, val: any) =>
    setFormData((prev) => ({ ...prev, [qId]: val }));
  const handleSubmit = () => {
    setPhase("success");
    setTimeout(() => setOpen(false), 2800);
  };

  const panelStyle: React.CSSProperties = {
    width: 380,
    maxHeight: 560,
    borderRadius: 16,
    background: bg,
    boxShadow: `0 32px 80px rgba(0,0,0,0.3), 0 0 0 1px ${bd}`,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    fontFamily: "'Inter', sans-serif",
  };

  const headerBlock = (
    title: string,
    subtitle?: string,
    onBack?: () => void,
  ) => (
    <div
      style={{
        padding: "14px 18px",
        borderBottom: `1px solid ${bd}`,
        flexShrink: 0,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {onBack && (
            <div
              onClick={onBack}
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: tx2,
                background: crd,
                fontSize: 14,
              }}
            >
              {"\u2190"}
            </div>
          )}
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: 6,
              background: `linear-gradient(135deg, ${bc}, ${bc}88)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 10,
              color: "#fff",
              fontWeight: 800,
            }}
          >
            S
          </div>
          <div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: tx,
                lineHeight: 1.2,
              }}
            >
              {title || config.companyName}
            </div>
            {subtitle && (
              <div style={{ fontSize: 10, color: tx3 }}>{subtitle}</div>
            )}
          </div>
        </div>
        <div
          onClick={() => setOpen(false)}
          style={{
            width: 28,
            height: 28,
            borderRadius: 6,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            color: tx2,
            background: crd,
          }}
        >
          {"\u2715"}
        </div>
      </div>
    </div>
  );

  // Question field renderer
  const QField = ({ q }: { q: CustomQuestion }) => (
    <div style={{ marginBottom: 12 }}>
      <label
        style={{
          fontSize: 10,
          fontWeight: 600,
          color: tx2,
          display: "block",
          marginBottom: 4,
        }}
      >
        {q.label} {q.required && <span style={{ color: "#FF4D6A" }}>*</span>}
        {q.condition && (
          <span
            style={{
              fontSize: 8,
              color: "#A78BFA",
              marginLeft: 4,
              fontWeight: 700,
            }}
          >
            {"\u25cf"} Conditional
          </span>
        )}
      </label>
      {q.type === "text" && (
        <input
          value={formData[q.id] || ""}
          onChange={(e) => updateField(q.id, e.target.value)}
          placeholder="Type here..."
          style={{
            width: "100%",
            padding: "9px 12px",
            borderRadius: 8,
            border: `1px solid ${bd}`,
            background: inp,
            color: tx,
            fontSize: 13,
            outline: "none",
            boxSizing: "border-box",
          }}
        />
      )}
      {q.type === "rating" && (
        <div style={{ display: "flex", gap: 4 }}>
          {[1, 2, 3, 4, 5].map((n) => (
            <div
              key={n}
              onClick={() => updateField(q.id, n)}
              style={{
                cursor: "pointer",
                color:
                  (formData[q.id] || 0) >= n
                    ? "#FBBF24"
                    : dk
                      ? "#3A4555"
                      : "#D0D5DD",
                transition: "all 0.15s",
                transform:
                  (formData[q.id] || 0) >= n ? "scale(1.15)" : "scale(1)",
              }}
            >
              {(formData[q.id] || 0) >= n ? (
                <StarIcon sx={{ fontSize: 20 }} />
              ) : (
                <StarBorderIcon sx={{ fontSize: 20 }} />
              )}
            </div>
          ))}
        </div>
      )}
      {q.type === "dropdown" && (
        <select
          value={formData[q.id] || ""}
          onChange={(e) => updateField(q.id, e.target.value)}
          style={{
            width: "100%",
            padding: "9px 12px",
            borderRadius: 8,
            border: `1px solid ${bd}`,
            background: inp,
            color: tx,
            fontSize: 13,
            outline: "none",
          }}
        >
          <option value="">Select...</option>
          {(q.options || []).map((o) => (
            <option key={o}>{o}</option>
          ))}
        </select>
      )}
      {q.type === "checkbox" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {(q.options || []).map((o) => (
            <label
              key={o}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 13,
                color: tx,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                style={{ accentColor: bc, width: 16, height: 16 }}
              />{" "}
              {o}
            </label>
          ))}
        </div>
      )}
    </div>
  );

  // Floating button
  const TriggerBtn = () =>
    config.position === "floating" ? (
      <div
        onClick={() => setOpen(!open)}
        style={{
          width: 54,
          height: 54,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${bc}, ${bc}cc)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          boxShadow: `0 8px 32px ${bc}44`,
          transition: "all 0.25s",
          transform: open ? "scale(0.9) rotate(90deg)" : "scale(1)",
          fontSize: 22,
          userSelect: "none",
          color: "#fff",
        }}
      >
        {open ? "\u2715" : "\ud83d\udcac"}
      </div>
    ) : (
      <div style={{ display: "flex", alignItems: "center" }}>
        <div
          onClick={() => setOpen(!open)}
          style={{
            writingMode: "vertical-rl",
            textOrientation: "mixed",
            background: `linear-gradient(180deg, ${bc}, ${bc}cc)`,
            color: "#fff",
            padding: "16px 10px",
            borderRadius: "10px 0 0 10px",
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
            whiteSpace: "nowrap",
            letterSpacing: 1,
            boxShadow: `0 4px 16px ${bc}33`,
          }}
        >
          {config.buttonText || "Feedback"}
        </div>
      </div>
    );

  if (!open) return <TriggerBtn />;

  // Side tab wrapper — shows panel + tab together for side mode
  const SideWrap: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    if (config.position !== "side") return <>{children}</>;
    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        {children}
        <div
          onClick={() => setOpen(false)}
          style={{
            writingMode: "vertical-rl",
            textOrientation: "mixed",
            background: `linear-gradient(180deg, ${bc}, ${bc}cc)`,
            color: "#fff",
            padding: "16px 10px",
            borderRadius: "0 10px 10px 0",
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
            whiteSpace: "nowrap",
            letterSpacing: 1,
            boxShadow: `4px 0 16px ${bc}22`,
            flexShrink: 0,
          }}
        >
          {config.buttonText || "Feedback"}
        </div>
      </div>
    );
  };

  // ─── PERMISSIONS ───
  if (phase === "permissions")
    return (
      <SideWrap>
        <div style={panelStyle}>
          {headerBlock("Screen Recording", "Permission required", () =>
            setPhase("form"),
          )}
          <div style={{ padding: "20px 22px", flex: 1, overflow: "auto" }}>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  background: `${bc}15`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 12px",
                  fontSize: 34,
                }}
              >
                {"\ud83c\udfa5"}
              </div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: tx,
                  marginBottom: 6,
                }}
              >
                Record Your Screen
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: tx2,
                  lineHeight: 1.6,
                  maxWidth: 280,
                  margin: "0 auto",
                }}
              >
                Show us exactly what&apos;s happening.
              </div>
            </div>
            <div
              style={{
                background: crd,
                borderRadius: 10,
                padding: 14,
                marginBottom: 14,
                border: `1px solid ${bd}`,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: tx3,
                  textTransform: "uppercase",
                  letterSpacing: "0.8px",
                  marginBottom: 10,
                }}
              >
                Auto-captured alongside video
              </div>
              {[
                { icon: "\ud83d\udda5", label: "Screen activity" },
                { icon: "\ud83d\udd27", label: "Console & network logs" },
                { icon: "\ud83d\udcf1", label: "Device info" },
                { icon: "\ud83d\uddb1", label: "Click tracking" },
              ].map((r, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "center",
                    padding: "5px 0",
                  }}
                >
                  <span style={{ fontSize: 16 }}>{r.icon}</span>
                  <div style={{ fontSize: 12, fontWeight: 600, color: tx }}>
                    {r.label}
                  </div>
                </div>
              ))}
            </div>
            <div
              onClick={() => setMicOn(!micOn)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 14px",
                borderRadius: 10,
                border: `1.5px solid ${micOn ? "#00E5A0" : bd}`,
                background: micOn ? "#00E5A010" : crd,
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 20 }}>
                  {micOn ? "\ud83c\udf99" : "\ud83d\udd07"}
                </span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: tx }}>
                    Mic narration
                  </div>
                  <div style={{ fontSize: 10, color: tx3 }}>
                    Explain while recording
                  </div>
                </div>
              </div>
              <div
                style={{
                  width: 38,
                  height: 22,
                  borderRadius: 11,
                  background: micOn ? "#00E5A0" : dk ? "#3A4E5E" : "#D0D5DD",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    background: "#fff",
                    position: "absolute",
                    top: 3,
                    left: micOn ? 19 : 3,
                    transition: "all 0.2s",
                  }}
                />
              </div>
            </div>
          </div>
          <div
            style={{
              padding: "14px 18px",
              borderTop: `1px solid ${bd}`,
              flexShrink: 0,
            }}
          >
            <button
              onClick={() => {
                setPhase("recording");
                setRecSec(0);
                setRecPaused(false);
              }}
              style={{
                width: "100%",
                padding: "13px 0",
                borderRadius: 10,
                border: "none",
                cursor: "pointer",
                background: "linear-gradient(135deg, #FF4D6A, #E8364E)",
                color: "#fff",
                fontSize: 14,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: "#fff",
                }}
              />{" "}
              Start Recording
            </button>
          </div>
        </div>
      </SideWrap>
    );

  // ─── RECORDING ───
  if (phase === "recording")
    return (
      <SideWrap>
        <div style={panelStyle}>
          <div
            style={{
              background: `linear-gradient(135deg, #FF4D6A20, #FF4D6A08)`,
              flexShrink: 0,
            }}
          >
            {headerBlock(
              "Recording...",
              micOn ? "\ud83c\udf99 Mic on" : "Show the issue",
            )}
          </div>
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "24px 22px",
            }}
          >
            <div
              style={{
                position: "relative",
                width: 140,
                height: 140,
                marginBottom: 20,
              }}
            >
              <svg
                width="140"
                height="140"
                style={{ transform: "rotate(-90deg)" }}
              >
                <circle
                  cx="70"
                  cy="70"
                  r="62"
                  fill="none"
                  stroke={dk ? "#1E2A38" : "#E5E9EF"}
                  strokeWidth="6"
                />
                <circle
                  cx="70"
                  cy="70"
                  r="62"
                  fill="none"
                  stroke={recPaused ? "#FBBF24" : "#FF4D6A"}
                  strokeWidth="6"
                  strokeDasharray={`${2 * Math.PI * 62}`}
                  strokeDashoffset={`${2 * Math.PI * 62 * (1 - recPct / 100)}`}
                  strokeLinecap="round"
                  style={{ transition: "stroke-dashoffset 1s linear" }}
                />
              </svg>
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {!recPaused && (
                  <FiberManualRecordIcon
                    sx={{
                      fontSize: 12,
                      color: "#FF4D6A",
                      mb: 0.5,
                      animation: "pulse 1.2s infinite",
                      "@keyframes pulse": {
                        "0%,100%": { opacity: 1 },
                        "50%": { opacity: 0.4 },
                      },
                    }}
                  />
                )}
                {recPaused && (
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#FBBF24",
                      marginBottom: 6,
                      textTransform: "uppercase",
                    }}
                  >
                    Paused
                  </div>
                )}
                <div
                  style={{
                    fontSize: 32,
                    fontWeight: 800,
                    color: tx,
                    fontFamily: "monospace",
                  }}
                >
                  {fmt(recSec)}
                </div>
                <div style={{ fontSize: 10, color: tx3 }}>/ {fmt(maxSec)}</div>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: 6,
                marginBottom: 20,
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              {[
                {
                  label: "Console",
                  color: "#FF4D6A",
                  count: Math.floor(recSec / 8),
                },
                {
                  label: "Network",
                  color: "#3B82F6",
                  count: Math.floor(recSec / 3),
                },
                {
                  label: "Clicks",
                  color: "#00E5A0",
                  count: Math.floor(recSec / 5),
                },
                ...(micOn
                  ? [
                      {
                        label: "Audio",
                        color: "#A78BFA",
                        count: "\u25cf" as any,
                      },
                    ]
                  : []),
              ].map((ind, i) => (
                <div
                  key={i}
                  style={{
                    padding: "4px 9px",
                    borderRadius: 6,
                    fontSize: 10,
                    fontWeight: 600,
                    background: ind.color + "15",
                    color: ind.color,
                    border: `1px solid ${ind.color}30`,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <span
                    style={{
                      width: 5,
                      height: 5,
                      borderRadius: "50%",
                      background: ind.color,
                    }}
                  />{" "}
                  {ind.label}: {ind.count}
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10, width: "100%" }}>
              <button
                onClick={() => setRecPaused(!recPaused)}
                style={{
                  flex: 1,
                  padding: "12px 0",
                  borderRadius: 10,
                  border: `1.5px solid ${bd}`,
                  background: crd,
                  color: tx,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {recPaused ? "\u25b6 Resume" : "\u23f8 Pause"}
              </button>
              <button
                onClick={() => {
                  setPhase("review");
                  setHasRecording(true);
                }}
                style={{
                  flex: 1,
                  padding: "12px 0",
                  borderRadius: 10,
                  border: "none",
                  background: "linear-gradient(135deg, #FF4D6A, #E8364E)",
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                {"\u23f9"} Stop
              </button>
            </div>
            <button
              onClick={() => {
                setPhase("form");
                setRecSec(0);
              }}
              style={{
                marginTop: 10,
                border: "none",
                background: "transparent",
                color: tx3,
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </SideWrap>
    );

  // ─── REVIEW ───
  if (phase === "review")
    return (
      <SideWrap>
        <div style={panelStyle}>
          {headerBlock("Review Recording", `${fmt(recSec)} captured`, () => {
            setPhase("form");
            setHasRecording(true);
          })}
          <div style={{ flex: 1, overflow: "auto", padding: "16px 18px" }}>
            <div
              style={{
                borderRadius: 10,
                overflow: "hidden",
                marginBottom: 14,
                border: `1px solid ${bd}`,
                position: "relative",
              }}
            >
              <div
                style={{
                  height: 150,
                  background: dk ? "#0B0F14" : "#1A2332",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    width: "80%",
                    height: "65%",
                    background: "#2A3544",
                    borderRadius: 6,
                    opacity: 0.5,
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <div
                      style={{
                        width: 0,
                        height: 0,
                        borderTop: "10px solid transparent",
                        borderBottom: "10px solid transparent",
                        borderLeft: "16px solid #fff",
                        marginLeft: 3,
                      }}
                    />
                  </div>
                </div>
                <div
                  style={{
                    position: "absolute",
                    bottom: 8,
                    right: 8,
                    padding: "3px 8px",
                    borderRadius: 4,
                    background: "rgba(0,0,0,0.7)",
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 600,
                    fontFamily: "monospace",
                  }}
                >
                  {fmt(recSec)}
                </div>
                <div
                  style={{
                    position: "absolute",
                    top: 8,
                    left: 8,
                    padding: "3px 8px",
                    borderRadius: 4,
                    background: "#FF4D6A",
                    color: "#fff",
                    fontSize: 9,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <span
                    style={{
                      width: 5,
                      height: 5,
                      borderRadius: "50%",
                      background: "#fff",
                    }}
                  />{" "}
                  REC
                </div>
              </div>
              <div
                style={{ height: 4, background: dk ? "#1A2332" : "#E5E9EF" }}
              >
                <div
                  style={{
                    height: "100%",
                    width: "100%",
                    background: `linear-gradient(90deg, ${bc}, #FF4D6A)`,
                    borderRadius: 2,
                  }}
                />
              </div>
            </div>
            <div
              style={{
                background: crd,
                borderRadius: 10,
                padding: 12,
                border: `1px solid ${bd}`,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: tx3,
                  textTransform: "uppercase",
                  marginBottom: 8,
                }}
              >
                Captured Data
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 6,
                }}
              >
                {[
                  {
                    l: "Console Errors",
                    v: `${Math.floor(recSec / 8)}`,
                    c: "#FF4D6A",
                  },
                  {
                    l: "Network Reqs",
                    v: `${Math.floor(recSec / 3)}`,
                    c: "#3B82F6",
                  },
                  {
                    l: "Click Events",
                    v: `${Math.floor(recSec / 5)}`,
                    c: "#00E5A0",
                  },
                  { l: "Duration", v: fmt(recSec), c: bc },
                ].map((d, i) => (
                  <div
                    key={i}
                    style={{
                      padding: "8px 10px",
                      borderRadius: 6,
                      background: dk ? "#0B0F14" : "#fff",
                      border: `1px solid ${bd}`,
                    }}
                  >
                    <div style={{ fontSize: 9, color: tx3, marginBottom: 2 }}>
                      {d.l}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: d.c }}>
                      {d.v}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div
            style={{
              padding: "12px 18px",
              borderTop: `1px solid ${bd}`,
              flexShrink: 0,
              display: "flex",
              gap: 8,
            }}
          >
            <button
              onClick={() => {
                setPhase("permissions");
                setRecSec(0);
                setHasRecording(false);
              }}
              style={{
                flex: 1,
                padding: "11px 0",
                borderRadius: 10,
                border: `1.5px solid ${bd}`,
                background: crd,
                color: tx,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {"\ud83d\udd04"} Re-record
            </button>
            <button
              onClick={() => {
                setHasRecording(true);
                setPhase("submit_form");
              }}
              style={{
                flex: 2,
                padding: "11px 0",
                borderRadius: 10,
                border: "none",
                background: `linear-gradient(135deg, ${bc}, ${bc}cc)`,
                color: "#fff",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {"\u2713"} Use Recording
            </button>
          </div>
        </div>
      </SideWrap>
    );

  // ─── SUBMIT FORM ───
  if (phase === "submit_form")
    return (
      <SideWrap>
        <div style={panelStyle}>
          {headerBlock(
            "Describe the Issue",
            hasRecording
              ? `Recording (${fmt(recSec)})`
              : hasScreenshot
                ? "Screenshot attached"
                : undefined,
            () => setPhase("form"),
          )}
          <div style={{ flex: 1, overflow: "auto", padding: "14px 18px" }}>
            {(hasRecording || hasScreenshot) && (
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                {hasRecording && (
                  <div
                    style={{
                      flex: 1,
                      padding: "10px 12px",
                      borderRadius: 8,
                      background: "#00E5A010",
                      border: "1.5px solid #00E5A040",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span style={{ fontSize: 18 }}>{"\ud83c\udfa5"}</span>
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#00E5A0",
                        }}
                      >
                        Recording
                      </div>
                      <div style={{ fontSize: 10, color: tx3 }}>
                        {fmt(recSec)} · {Math.floor(recSec / 8)} errors
                      </div>
                    </div>
                  </div>
                )}
                {hasScreenshot && (
                  <div
                    style={{
                      flex: 1,
                      padding: "10px 12px",
                      borderRadius: 8,
                      background: "#3B82F610",
                      border: "1.5px solid #3B82F640",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span style={{ fontSize: 18 }}>{"\ud83d\udcf8"}</span>
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#3B82F6",
                        }}
                      >
                        Screenshot
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            {config.collectEmail && (
              <div style={{ marginBottom: 12 }}>
                <label
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: tx2,
                    display: "block",
                    marginBottom: 4,
                  }}
                >
                  Email
                </label>
                <input
                  value=""
                  placeholder="you@company.com"
                  style={{
                    width: "100%",
                    padding: "9px 12px",
                    borderRadius: 8,
                    border: `1px solid ${bd}`,
                    background: inp,
                    color: tx,
                    fontSize: 13,
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            )}
            <div style={{ marginBottom: 12 }}>
              <label
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: tx2,
                  display: "block",
                  marginBottom: 4,
                }}
              >
                {tab === "bugReport"
                  ? "What went wrong?"
                  : tab === "featureRequest"
                    ? "Describe your idea"
                    : "Your feedback"}{" "}
                *
              </label>
              <textarea
                rows={2}
                placeholder="Describe the issue..."
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  borderRadius: 8,
                  border: `1px solid ${bd}`,
                  background: inp,
                  color: tx,
                  fontSize: 13,
                  outline: "none",
                  resize: "vertical",
                  lineHeight: 1.5,
                  boxSizing: "border-box",
                }}
              />
            </div>
            {visibleQs.map((q) => (
              <QField key={q.id} q={q} />
            ))}
          </div>
          <div
            style={{
              padding: "12px 18px",
              borderTop: `1px solid ${bd}`,
              flexShrink: 0,
            }}
          >
            <button
              onClick={handleSubmit}
              style={{
                width: "100%",
                padding: "12px 0",
                borderRadius: 10,
                border: "none",
                cursor: "pointer",
                background: `linear-gradient(135deg, ${bc}, ${bc}cc)`,
                color: "#fff",
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              Submit
            </button>
            {config.poweredBy && (
              <div
                style={{
                  textAlign: "center",
                  marginTop: 8,
                  fontSize: 10,
                  color: tx3,
                }}
              >
                Powered by{" "}
                <span style={{ fontWeight: 700, color: bc }}>Sniffer</span>
              </div>
            )}
          </div>
        </div>
      </SideWrap>
    );

  // ─── SUCCESS ───
  if (phase === "success")
    return (
      <SideWrap>
        <div style={panelStyle}>
          <div style={{ padding: "52px 32px", textAlign: "center" }}>
            <div
              style={{
                width: 68,
                height: 68,
                borderRadius: "50%",
                background: "#00E5A018",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
              }}
            >
              <CheckIcon sx={{ fontSize: 32, color: "#00E5A0" }} />
            </div>
            <div
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: tx,
                marginBottom: 8,
              }}
            >
              Thank you!
            </div>
            <div style={{ fontSize: 13, color: tx2, lineHeight: 1.5 }}>
              Submitted with full technical context.
            </div>
            {hasRecording && (
              <div
                style={{
                  marginTop: 12,
                  padding: "8px 14px",
                  borderRadius: 8,
                  background: "#00E5A010",
                  border: "1px solid #00E5A030",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#00E5A0",
                }}
              >
                {"\ud83c\udfa5"} Recording · {"\ud83d\udd27"} Console ·{" "}
                {"\ud83d\uddb1"} Clicks
              </div>
            )}
          </div>
        </div>
      </SideWrap>
    );

  // ─── MAIN FORM ───
  return (
    <SideWrap>
      <div style={panelStyle}>
        {headerBlock(config.companyName)}
        {tabs.length > 1 && (
          <div
            style={{
              padding: "0 18px",
              borderBottom: `1px solid ${bd}`,
              flexShrink: 0,
            }}
          >
            <div style={{ display: "flex", gap: 2 }}>
              {tabs.map((t) => (
                <div
                  key={t.id}
                  onClick={() => {
                    setTab(t.id);
                    setFormData({});
                  }}
                  style={{
                    flex: 1,
                    padding: "10px 4px",
                    textAlign: "center",
                    cursor: "pointer",
                    fontSize: 11,
                    fontWeight: 600,
                    borderBottom: `2px solid ${tab === t.id ? bc : "transparent"}`,
                    color: tab === t.id ? tx : tx3,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 4,
                  }}
                >
                  {t.id === "bugReport" && (
                    <BugReportOutlinedIcon sx={{ fontSize: 13 }} />
                  )}
                  {t.id === "generalFeedback" && (
                    <ChatOutlinedIcon sx={{ fontSize: 13 }} />
                  )}
                  {t.id === "featureRequest" && (
                    <LightbulbOutlinedIcon sx={{ fontSize: 13 }} />
                  )}
                  {t.label}
                </div>
              ))}
            </div>
          </div>
        )}
        <div style={{ flex: 1, overflow: "auto", padding: "16px 18px" }}>
          <div style={{ marginBottom: 14 }}>
            <label
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: tx3,
                display: "block",
                marginBottom: 8,
                textTransform: "uppercase",
                letterSpacing: "0.8px",
              }}
            >
              {tab === "bugReport"
                ? "Show us what's happening"
                : "Capture context"}
            </label>
            <div
              onClick={() => setPhase("permissions")}
              style={{
                padding: 16,
                borderRadius: 12,
                cursor: "pointer",
                marginBottom: 8,
                background: hasRecording
                  ? "#00E5A010"
                  : `linear-gradient(135deg, #FF4D6A12, #FF4D6A06)`,
                border: `1.5px solid ${hasRecording ? "#00E5A050" : "#FF4D6A35"}`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    background: hasRecording ? "#00E5A020" : "#FF4D6A18",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 22,
                    flexShrink: 0,
                  }}
                >
                  {hasRecording ? "\u2705" : "\ud83c\udfa5"}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: tx,
                      marginBottom: 2,
                    }}
                  >
                    {hasRecording
                      ? "Recording attached!"
                      : "Record Your Screen"}
                  </div>
                  <div style={{ fontSize: 11, color: tx3, lineHeight: 1.4 }}>
                    {hasRecording
                      ? `${fmt(recSec)} with console, network & clicks`
                      : "Auto-captures console, network, clicks & device"}
                  </div>
                </div>
                <div style={{ color: tx3, fontSize: 18 }}>{"\u2192"}</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <div
                onClick={() => setHasScreenshot(!hasScreenshot)}
                style={{
                  flex: 1,
                  padding: 12,
                  borderRadius: 10,
                  cursor: "pointer",
                  textAlign: "center",
                  border: `1.5px solid ${hasScreenshot ? "#3B82F650" : bd}`,
                  background: hasScreenshot ? "#3B82F610" : crd,
                }}
              >
                <div style={{ fontSize: 20, marginBottom: 3 }}>
                  {hasScreenshot ? "\u2705" : "\ud83d\udcf8"}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: hasScreenshot ? "#3B82F6" : tx2,
                  }}
                >
                  {hasScreenshot ? "Captured!" : "Screenshot"}
                </div>
              </div>
              <div
                onClick={() => setPhase("submit_form")}
                style={{
                  flex: 1,
                  padding: 12,
                  borderRadius: 10,
                  cursor: "pointer",
                  textAlign: "center",
                  border: `1.5px solid ${bd}`,
                  background: crd,
                }}
              >
                <div style={{ fontSize: 20, marginBottom: 3 }}>
                  {"\u270d\ufe0f"}
                </div>
                <div style={{ fontSize: 11, fontWeight: 600, color: tx2 }}>
                  Text Only
                </div>
              </div>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: tx2,
                display: "block",
                marginBottom: 4,
              }}
            >
              Quick description
            </label>
            <textarea
              placeholder="What went wrong?"
              rows={2}
              style={{
                width: "100%",
                padding: "9px 12px",
                borderRadius: 8,
                border: `1px solid ${bd}`,
                background: inp,
                color: tx,
                fontSize: 13,
                outline: "none",
                resize: "none",
                lineHeight: 1.5,
                boxSizing: "border-box",
              }}
            />
          </div>
          <div
            style={{
              padding: "8px 10px",
              borderRadius: 8,
              background: crd,
              border: `1px solid ${bd}`,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                marginBottom: 4,
              }}
            >
              <span style={{ fontSize: 9, fontWeight: 600, color: tx3 }}>
                Auto-captured
              </span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
              {["Browser", "OS", "URL", "Console", "Network", "Screen"].map(
                (x) => (
                  <span
                    key={x}
                    style={{
                      fontSize: 8,
                      padding: "2px 6px",
                      borderRadius: 3,
                      background: dk ? "#1A2332" : "#EDF0F4",
                      color: tx3,
                      fontWeight: 600,
                    }}
                  >
                    {x}
                  </span>
                ),
              )}
            </div>
          </div>
        </div>
        <div
          style={{
            padding: "12px 18px",
            borderTop: `1px solid ${bd}`,
            flexShrink: 0,
          }}
        >
          <button
            onClick={() => {
              if (hasRecording || hasScreenshot) handleSubmit();
              else setPhase("submit_form");
            }}
            style={{
              width: "100%",
              padding: "12px 0",
              borderRadius: 10,
              border: "none",
              cursor: "pointer",
              background: `linear-gradient(135deg, ${bc}, ${bc}cc)`,
              color: "#fff",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {hasRecording || hasScreenshot
              ? "Submit with Capture"
              : "Continue \u2192"}
          </button>
          {config.poweredBy && (
            <div
              style={{
                textAlign: "center",
                marginTop: 8,
                fontSize: 10,
                color: tx3,
              }}
            >
              Powered by{" "}
              <span style={{ fontWeight: 700, color: bc }}>Sniffer</span>
            </div>
          )}
        </div>
      </div>
    </SideWrap>
  );
};

export default WidgetPreview;
